import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { FileText, Plus, Trash2, Users, Edit3, CheckCircle2, Clock, Loader2 } from "lucide-react";

export default function DocumentsPage() {
    const [tab, setTab] = useState("templates");
    const [templates, setTemplates] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [clients, setClients] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [assignFor, setAssignFor] = useState(null);
    const confirm = useConfirm();
    const nav = useNavigate();

    const load = async () => {
        setBusy(true);
        try {
            const [t, a, c] = await Promise.all([
                api.get("/documents/templates"),
                api.get("/documents/assignments"),
                api.get("/clients"),
            ]);
            setTemplates(t.data); setSubmissions(a.data); setClients(c.data);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    useEffect(() => { load(); }, []);

    const createTemplate = async () => {
        const { data } = await api.post("/documents/templates", {
            title: "Untitled document",
            description: "",
            fields: [
                { id: `f_${Date.now()}`, label: "Full name", type: "text", required: true, placeholder: "", options: [] },
            ],
            is_default: false,
            require_signature: true,
        });
        nav(`/documents/${data.id}`);
    };

    const del = async (t) => {
        const ok = await confirm({
            title: "Delete this template?",
            message: `"${t.title}" will be removed. Pending assignments will also be deleted.`,
            confirmLabel: "Delete",
        });
        if (!ok) return;
        await api.delete(`/documents/templates/${t.id}`);
        load();
    };

    const pendingCount = submissions.filter((s) => s.status === "submitted" && !s.acknowledged_by_coach).length;

    return (
        <div className="p-8 space-y-6" data-testid="documents-page">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <p className="label-tiny mb-2">/ Onboarding</p>
                    <h1 className="font-serif text-5xl">Documents</h1>
                    <p className="text-sm text-[var(--text-muted)] mt-2">
                        Build custom intake forms, waivers and agreements. Auto-assigned to every new client.
                    </p>
                </div>
                <button onClick={createTemplate} className="btn-primary inline-flex items-center gap-2" data-testid="new-template-btn">
                    <Plus size={14} /> New template
                </button>
            </div>

            <div className="flex gap-px bg-[var(--border)] max-w-lg">
                <button onClick={() => setTab("templates")}
                    className={`flex-1 py-3 text-xs font-mono tracking-[0.25em] uppercase ${tab==="templates"?"bg-[var(--surface-hover)] text-[var(--gold)]":"bg-[var(--surface)] text-[var(--text-muted)]"}`}
                    data-testid="tab-templates">Templates</button>
                <button onClick={() => setTab("submissions")}
                    className={`flex-1 py-3 text-xs font-mono tracking-[0.25em] uppercase ${tab==="submissions"?"bg-[var(--surface-hover)] text-[var(--gold)]":"bg-[var(--surface)] text-[var(--text-muted)]"}`}
                    data-testid="tab-submissions">
                    Submissions {pendingCount > 0 && <span className="ml-1 px-1.5 py-0.5 bg-[var(--danger)] text-white text-[10px]">{pendingCount}</span>}
                </button>
            </div>

            {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
            {busy && <div className="text-sm text-[var(--text-muted)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

            {tab === "templates" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
                    {templates.map((t) => (
                        <div key={t.id} className="bg-[var(--surface)] p-5 flex flex-col" data-testid={`tpl-${t.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <FileText size={18} className="text-[var(--gold)]" />
                                {t.is_default && <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-[var(--gold-bright)]">Default</span>}
                            </div>
                            <h3 className="font-serif text-2xl mb-1">{t.title}</h3>
                            <p className="text-xs text-[var(--text-muted)] mb-4 flex-1">{t.description || "—"}</p>
                            <p className="text-[10px] font-mono text-[var(--text-faint)] mb-3">{(t.fields||[]).length} fields · {t.require_signature ? "signature required" : "no signature"}</p>
                            <div className="flex gap-2">
                                <Link to={`/documents/${t.id}`} className="btn-ghost flex-1 text-center text-xs inline-flex items-center justify-center gap-1"><Edit3 size={12} /> Edit</Link>
                                <button onClick={() => setAssignFor(t)} className="btn-ghost flex-1 text-xs inline-flex items-center justify-center gap-1" data-testid={`assign-${t.id}`}><Users size={12} /> Assign</button>
                                {!t.is_default && (
                                    <button onClick={() => del(t)} className="p-2 text-[var(--text-muted)] hover:text-[var(--danger)]" data-testid={`del-${t.id}`}><Trash2 size={12} /></button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "submissions" && (
                <div className="border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[var(--surface-2)]">
                            <tr className="text-left font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--text-muted)]">
                                <th className="p-3">Client</th>
                                <th className="p-3">Document</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Assigned</th>
                                <th className="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {submissions.length === 0 && (
                                <tr><td colSpan={5} className="p-8 text-center text-[var(--text-muted)]">No assignments yet.</td></tr>
                            )}
                            {submissions.map((s) => (
                                <tr key={s.id} className="border-t border-[var(--border)]" data-testid={`sub-${s.id}`}>
                                    <td className="p-3">{s.client_name}</td>
                                    <td className="p-3">{s.template_title}</td>
                                    <td className="p-3">
                                        {s.status === "submitted"
                                            ? <span className="inline-flex items-center gap-1 text-[var(--gold-bright)] font-mono text-xs"><CheckCircle2 size={12} /> submitted</span>
                                            : <span className="inline-flex items-center gap-1 text-[var(--text-muted)] font-mono text-xs"><Clock size={12} /> pending</span>}
                                        {s.status === "submitted" && !s.acknowledged_by_coach && (
                                            <span className="ml-2 px-1.5 py-0.5 bg-[var(--danger)] text-white text-[10px] font-mono">NEW</span>
                                        )}
                                    </td>
                                    <td className="p-3 font-mono text-xs text-[var(--text-muted)]">{new Date(s.assigned_at).toLocaleDateString()}</td>
                                    <td className="p-3 text-right">
                                        <Link to={`/documents/submission/${s.id}`} className="btn-ghost text-xs" data-testid={`view-sub-${s.id}`}>View</Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {assignFor && (
                <AssignModal template={assignFor} clients={clients} onClose={() => setAssignFor(null)} onDone={load} />
            )}
        </div>
    );
}

function AssignModal({ template, clients, onClose, onDone }) {
    const [selected, setSelected] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        if (selected.length === 0) { setErr("Pick at least one client"); return; }
        setBusy(true); setErr("");
        try {
            await api.post("/documents/assign", { template_id: template.id, client_ids: selected });
            onDone(); onClose();
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="assign-doc-modal">
                <div className="p-5 border-b border-[var(--border)]">
                    <p className="label-tiny mb-1">/ Assign</p>
                    <h3 className="font-serif text-2xl">{template.title}</h3>
                </div>
                <div className="flex-1 overflow-auto p-3 space-y-1">
                    {clients.map((c) => (
                        <label key={c.id} className="flex items-center gap-3 p-2 hover:bg-[var(--surface-hover)] cursor-pointer" data-testid={`assign-client-${c.id}`}>
                            <input type="checkbox" checked={selected.includes(c.id)}
                                onChange={(e) => setSelected(e.target.checked ? [...selected, c.id] : selected.filter((x) => x !== c.id))} />
                            <span className="text-sm">{c.name}</span>
                        </label>
                    ))}
                </div>
                {err && <div className="p-3 text-sm text-[var(--danger)]">{err}</div>}
                <div className="border-t border-[var(--border)] p-4 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-ghost">Cancel</button>
                    <button onClick={submit} disabled={busy} className="btn-primary" data-testid="assign-confirm">
                        {busy ? "Assigning…" : `Assign to ${selected.length}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
